interface BackspaceContext {
  key: string
  separator: string
  selectionStart: number | null
  selectionEnd: number | null
  inputValue: string
  firstPartPattern?: string
}

type BackspaceResult =
  | { handled: false }
  | {
    handled: true
    nextRawValue: string
    targetDigitsLeft: number
    placeCursorAfterSeparator?: boolean
  }

const DAYS_PATTERN = 'dd'
const MONTHS_PATTERN = 'mm'

function isDigit(char: string | undefined): boolean {
  return char !== undefined && /\d/.test(char)
}

export function computeBackspaceBehavior({
  key,
  separator,
  selectionStart,
  selectionEnd,
  inputValue,
  firstPartPattern,
}: BackspaceContext): BackspaceResult {
  if (
    key !== 'Backspace'
    || !separator
    || selectionStart === null
    || selectionStart !== selectionEnd
    || selectionStart <= 0
  ) {
    return { handled: false }
  }

  const cursor = selectionStart
  const precedingSeparatorIndex = inputValue.lastIndexOf(separator, cursor - 1)
  const hasSingleDigitTrailingSegment = cursor === inputValue.length
    && precedingSeparatorIndex >= 0
    && cursor - precedingSeparatorIndex === 2
    && isDigit(inputValue[cursor - 1])

  if (hasSingleDigitTrailingSegment) {
    return {
      handled: true,
      nextRawValue: inputValue.slice(0, cursor - 1),
      targetDigitsLeft: Math.max(0, countDigitsBefore(inputValue, cursor) - 1),
      placeCursorAfterSeparator: true,
    }
  }

  if (inputValue[cursor - 1] !== separator) {
    return { handled: false }
  }

  const segmentStart = findSegmentStart(inputValue, cursor - 2)
  const segment = inputValue.slice(segmentStart, cursor - 1)
  const suffix = inputValue.slice(cursor)
  const isTrailingFirstPartSeparator = suffix === '' && segmentStart === 0
  const isTwoDigitDatePart = firstPartPattern === DAYS_PATTERN || firstPartPattern === MONTHS_PATTERN

  if (isTrailingFirstPartSeparator && isTwoDigitDatePart && segment.length === 2 && segment[0] === '0') {
    const digit = segment[1]

    if (isDigit(digit) && digit !== '0') {
      return { handled: true, nextRawValue: '0', targetDigitsLeft: 1 }
    }
  }

  const deletedDigitIndex = findPreviousDigitIndex(inputValue, cursor - 2)

  if (deletedDigitIndex < 0) {
    return { handled: false }
  }

  const nextRawValue = isTrailingFirstPartSeparator
    ? inputValue.slice(0, deletedDigitIndex)
    : `${inputValue.slice(0, deletedDigitIndex)}${inputValue.slice(deletedDigitIndex + 1)}`
  const targetDigitsLeft = Math.max(0, countDigitsBefore(inputValue, cursor) - 1)

  return {
    handled: true,
    nextRawValue,
    targetDigitsLeft,
  }
}

export function mapDigitsToCursorPosition(
  value: string,
  digitsLeft: number,
  placeAfterFollowingSeparator = false,
): number {
  if (digitsLeft <= 0) {
    return 0
  }

  let seenDigits = 0

  for (let index = 0; index < value.length; index += 1) {
    if (isDigit(value[index])) {
      seenDigits += 1
    }

    if (seenDigits >= digitsLeft) {
      const positionAfterDigit = index + 1
      const hasFollowingSeparator = !isDigit(value[positionAfterDigit])
        && value[positionAfterDigit] !== undefined

      return placeAfterFollowingSeparator && hasFollowingSeparator
        ? positionAfterDigit + 1
        : positionAfterDigit
    }
  }

  return value.length
}

function countDigitsBefore(value: string, end: number): number {
  let count = 0
  for (let index = 0; index < end; index += 1) {
    if (isDigit(value[index])) {
      count += 1
    }
  }
  return count
}

function findPreviousDigitIndex(value: string, fromIndex: number): number {
  for (let index = fromIndex; index >= 0; index -= 1) {
    if (isDigit(value[index])) {
      return index
    }
  }

  return -1
}

function findSegmentStart(value: string, beforeSeparatorIndex: number): number {
  for (let index = beforeSeparatorIndex; index >= 0; index -= 1) {
    if (!isDigit(value[index])) {
      return index + 1
    }
  }

  return 0
}

export type { BackspaceContext, BackspaceResult }
